// --- React
import React from "react";
// --- Styles
import styles from "./styles.module.scss";

/**
 * TwoRowsLayout Component
 *
 * A layout wrapper that organizes content into a two-row grid structure.
 * Used across multiple pages for consistent page layout.
 *
 * Supports two variants:
 * 1. Single-column (default): top + bottom panels
 * 2. Two-column: top + bottom-left/bottom-right panels
 *
 * @component
 * @param {Object} props - Component properties
 * @param {React.ReactNode} props.children - Elements to be wrapped inside the layout
 * @param {string} [props.className] - Additional CSS class names
 * @param {boolean} [props.twoColumn=false] - Use two-column layout for bottom row
 * @returns {JSX.Element} A styled section containing child components
 *
 * @example
 * // Single-column layout (default - most pages)
 * <TwoRowsLayout>
 *   <div data-position="top">Search Form</div>
 *   <div data-position="bottom">Data Grid</div>
 * </TwoRowsLayout>
 *
 * @example
 * // Two-column layout (Home page)
 * <TwoRowsLayout twoColumn>
 *   <div data-position="top">Search Form</div>
 *   <div className="bottom-row">
 *     <div data-position="bottom-left">Left Panel</div>
 *     <div data-position="bottom-right">Right Panel</div>
 *   </div>
 * </TwoRowsLayout>
 */
export const TwoRowsLayout = ({
  children,
  className = "",
  twoColumn = false,
}) => {
  const classNames = [
    styles.container,
    twoColumn ? styles.twoColumn : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return <section className={classNames}>{children}</section>;
};

export default TwoRowsLayout;
